<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="hidden md:block"></div>
            <h1 class="text-3xl text-center">
                Imágenes compartidas
                <strong class="text-theme-primary">
                    {{ itemsNumber }}
                </strong>
            </h1>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="$ability.hasPermission('sharedImage:Create')"
                    color="success"
                    size="large"
                    @click="isOpenCreateDialog = true"
                >
                    Nueva imagen
                </app-button>
            </div>
        </div>

        <p class="mt-2 text-center text-sm text-theme-muted">
            Todo lo que se sube acá queda disponible para cualquier tenant desde
            "Ver banco de imágenes compartidas" en el selector de imágenes.
        </p>

        <app-dialog
            v-model="isOpenCreateDialog"
            size="lg"
            @close="onCreateDialogClose"
        >
            <template #header>
                <h2 class="text-xl text-center">Nueva imagen compartida</h2>
            </template>
            <image-creator
                use-shared-endpoints
                hide-image-rights-notice
                @created="onImageCreated"
                @loading="onImageLoading"
            />
        </app-dialog>

        <div
            class="mb-6 mt-2 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
        >
            <div class="flex items-center w-full lg:max-w-lg">
                <custom-input
                    v-model="keyword"
                    type="text"
                    placeholder="Buscar imágenes..."
                    class="flex-1"
                    @click-icon="reloadItems"
                    @enter="reloadItems"
                >
                    <template #icon>🔎</template>
                </custom-input>

                <app-button
                    relief
                    class="ml-3 mt-1 px-5 whitespace-nowrap"
                    @click="reloadItems"
                >
                    Buscar
                </app-button>
            </div>

            <div class="flex justify-center lg:justify-end">
                <custom-pagination
                    v-model="currentPage"
                    :length="pagesNumber"
                    @input="listItems()"
                />
            </div>
        </div>

        <div
            class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-5"
        >
            <div
                v-for="item in itemsCurrent"
                :key="item.id"
                class="group overflow-hidden rounded-xl border border-stack-primary bg-white shadow-sm hover:shadow-lg transition-all duration-300"
            >
                <div class="relative overflow-hidden">
                    <img
                        v-img:group
                        v-img-loading
                        loading="lazy"
                        :src="item.url | image"
                        :alt="item.alt"
                        class="w-full h-56 object-cover transition duration-500 group-hover:scale-105"
                    />
                </div>

                <div class="bg-brand-blue px-4 py-3">
                    <div class="flex items-center justify-between">
                        <div class="flex-1 min-w-0 pr-3">
                            <div
                                class="font-semibold text-white truncate"
                                :title="item.alt"
                            >
                                {{ item.alt || "Sin nombre" }}
                            </div>

                            <div
                                class="mt-2 flex items-center text-xs text-blue-100"
                            >
                                <span>🖼 {{ imageSizeLabel(item.size) }}</span>
                            </div>

                            <div class="mt-1 text-xs text-blue-200 truncate">
                                📅 {{ item.updatedAt | date }}
                            </div>
                        </div>

                        <button
                            class="flex-shrink-0 w-10 h-10 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 hover:scale-105 transition flex items-center justify-center"
                            title="Ver imagen"
                            @click="
                                $router.push(`/app/images/detail/${item.id}`)
                            "
                        >
                            <svg
                                class="w-5 h-5 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M16.5 3.5a2.121 2.121 0 113 3L12 14l-4 1 1-4 7.5-7.5z"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!itemsCurrent.length" class="w-11/12 mx-auto mt-4">
            <strong>
                Todavía no hay imágenes en el banco compartido, o ninguna
                coincide con la búsqueda.
            </strong>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { imageSizeLabel } from "@/common/fields/sizes-images";

import AppDialog from "@/components/utils/AppDialog.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImageCreator from "@/components/utils/ImageCreator.vue";

export default {
    name: "SharedImagesIndex",
    components: { AppDialog, CustomInput, ImageCreator },
    layout: "app",
    data: () => ({
        itemsCurrent: [],
        isOpenCreateDialog: false,
        createdInDialog: false,

        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        backup: {
            items: [],
            pagination: { pagesNumber: 1, itemsNumber: 1 }
        },
        keyword: ""
    }),
    computed: {
        ...mapState("images", ["images"]),
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {
        keyword(value) {
            if (value) {
                const lower = value.toLowerCase();
                this.itemsCurrent = this.images.filter((item) =>
                    item.alt.toLowerCase().includes(lower)
                );
                this.itemsNumber = this.itemsCurrent.length;
                this.pagesNumber = 1;
                this.currentPage = 1;
            } else {
                this.itemsCurrent = [...this.backup.items];
                this.itemsNumber = Number(this.backup.pagination.itemsNumber);
                this.pagesNumber = Number(this.backup.pagination.pagesNumber);
            }
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("sharedImage:Create", "/app"))) return;
        this.listItems(true);
    },
    methods: {
        ...mapActions("images", ["listSharedImagesPaginated"]),
        imageSizeLabel,
        async listItems(complete = false) {
            this.$loading.show();
            try {
                const { items, pagination } =
                    await this.listSharedImagesPaginated({
                        page: this.currentPage,
                        keyword: this.keyword,
                        complete
                    });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.backup = {
                    items,
                    pagination: {
                        itemsNumber: Number(this.itemsNumber),
                        pagesNumber: Number(this.pagesNumber)
                    }
                };
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onImageLoading(isLoading) {
            if (isLoading) {
                this.$loading.show();
                return;
            }
            if (this.isOpenCreateDialog) {
                this.$loading.hide();
            }
        },
        onImageCreated() {
            this.createdInDialog = true;
            this.isOpenCreateDialog = false;
        },
        onCreateDialogClose() {
            const created = this.createdInDialog;
            this.createdInDialog = false;
            if (created) {
                this.reloadItems();
            }
        }
    }
};
</script>
